import api from '@/lib/services/general.service'
import { waitingBySubject, openWaitingCount, overdueWaitingCount } from '@/lib/waitingStatus'

// The waiting rows the user has open (busy-timeline-srv#26): a thread or task somebody else owes
// them an answer on, with the date it was expected by.
//
// Additive on purpose. Everything that reads `timeline.waitingOn` keeps working untouched — that
// flag is still written, as a projection of these rows — and this module is what a surface asks when
// it wants the EXPECTED DATE, the owner and the ladder rung as well.
//
// A backend that has not shipped the endpoint yet is a normal outcome, not an error: `available`
// goes false and every caller falls back to what it did before (the Briefing keeps its own count,
// the list keeps the plain flag). Nothing here ever throws into a view.

export const state = {
  rows: [],
  isLoading: false,
  loaded: false,
  // False once GET v1/waiting has failed — the surfaces then use their existing fallbacks rather
  // than showing an empty "Waiting for replies".
  available: true,
}

export const getters = {
  rows: s => s.rows,
  isLoading: s => s.isLoading,
  loaded: s => s.loaded,
  available: s => s.available,
  // Indexed by the thread/task the row is about, for a listing that has rows of its own.
  bySubject: s => waitingBySubject(s.rows),
  openCount: s => openWaitingCount(s.rows),
  overdueCount: s => overdueWaitingCount(s.rows),
}

export const actions = {
  // The open waits. `state` is the API's own filter: open (default) | overdue | all.
  async fetch({ commit }, filter = 'open') {
    commit('SET_LOADING', true)
    try {
      const response = await api.get('waiting', { state: filter })
      const data = response?.data?.data ?? response?.data ?? []
      commit('SET_ROWS', Array.isArray(data) ? data : [])
      commit('SET_AVAILABLE', true)
    } catch {
      commit('SET_ROWS', [])
      commit('SET_AVAILABLE', false)
    } finally {
      commit('SET_LOADING', false)
      commit('SET_LOADED', true)
    }
  },

  // "I am waiting on this." The row comes back with its ladder rung already decided.
  async open({ commit }, { subjectId, subjectType = 'Timeline', expectedByUtc = null, owner = null }) {
    if (!subjectId) return null
    try {
      const response = await api.insert('waiting', { subjectId, subjectType, expectedByUtc, owner })
      const row = response?.data?.data ?? response?.data ?? null
      if (row) commit('UPSERT_ROW', row)
      return row
    } catch {
      return null
    }
  },

  // "No longer waiting" — or "they replied", when something else observed the reply.
  async resolve({ commit }, { id, by = 'user-cleared' }) {
    if (!id) return null
    try {
      const response = await api.insert(`waiting/${id}/resolve`, { by })
      const row = response?.data?.data ?? response?.data ?? null
      commit('REMOVE_ROW', id)
      return row
    } catch {
      return null
    }
  },

  // "Remind me" — a reminder is not the expected date, and setting one never moves it.
  async remind({ commit }, { id, remindAtUtc }) {
    if (!id) return null
    try {
      const response = await api.insert(`waiting/${id}/remind`, { remindAtUtc })
      const row = response?.data?.data ?? response?.data ?? null
      if (row) commit('UPSERT_ROW', row)
      return row
    } catch {
      return null
    }
  },

  // The words for a nudge: { subject, greeting, line }. The COMPOSE itself is the existing reply
  // flow — this only supplies what to put in it.
  async followUp(_, id) {
    if (!id) return null
    try {
      const response = await api.insert(`waiting/${id}/follow-up`, {})
      return response?.data?.data ?? response?.data ?? null
    } catch {
      return null
    }
  },
}

export const mutations = {
  SET_ROWS(s, rows) { s.rows = rows },
  SET_LOADING(s, value) { s.isLoading = value },
  SET_LOADED(s, value) { s.loaded = value },
  SET_AVAILABLE(s, value) { s.available = value },
  UPSERT_ROW(s, row) {
    const i = s.rows.findIndex(r => r.id === row.id)
    if (i >= 0) s.rows.splice(i, 1, row)
    else s.rows.push(row)
  },
  REMOVE_ROW(s, id) {
    s.rows = s.rows.filter(r => r.id !== id)
  },
}
